// W-A0 · Segment wedge-edge drag-resize — PURE geometry + redistribution math.
//
// DECK: design-handoff/concepts/segment-resize/{01-resize-mode,02-mid-drag,
// 03-mid-drag-distribute}.html (generator: harness/elevations/
// notes9-segment-resize-headercleanup.elevation.mjs). "Drag any boundary handle
// to trade width between its two Segments." Two modes via the "Drag affects"
// control (Shift toggles mid-drag):
//   • NEIGHBOR (default): the dragged boundary trades width between its TWO
//     adjacent Segments only — every other Segment keeps its size.
//   • ALL UNLOCKED: the grower gains; taken EQUALLY from every OTHER unlocked
//     Segment (each donor gives the same share); locked Segments never move.
//     Deck example: Career +8%, four donors −2% each (a 2% boundary move = 8%
//     gain = 4 donors × 2%).
// Min span (deck / real app): value floor 1% (Math.max(1, …)); locked kept.
//
// NO NEW DATA MODEL: operates on the existing `value` (% of wheel, ~sum 100) +
// `resize_lock` fields the right-rail sliders already edit. The overlay persists
// the returned array through the SAME setWheelSegments / withHistory path.
//
// Angle convention (matches the canonical renderer — createPie.js
// `d3.pie().value(d=>d.value).sort(null)` + d3.arc, and the deck's
// pt(a,r)=[r·sin a, −r·cos a]): 0° = north/top, increasing CLOCKWISE. Segments
// lay out in ARRAY ORDER; boundary i sits at the cumulative angle through
// segment i and separates segment i from segment (i+1) mod n.

export const MIN_SPAN_PCT = 1; // real-app floor: value min 1 (Math.max(1, …))
export const DEG_PER_TURN = 360;

const sum = (nums) => nums.reduce((acc, n) => acc + n, 0);
const round2 = (n) => Number(Number(n).toFixed(2));

/**
 * Screen-space pointer → angle in degrees [0, 360), north = 0, clockwise.
 * @param {number} cx wheel centre x (screen px)
 * @param {number} cy wheel centre y (screen px)
 * @param {number} px pointer x (screen px)
 * @param {number} py pointer y (screen px)
 */
export function pointerAngleDeg(cx, cy, px, py) {
  const dx = px - cx;
  const dy = py - cy;
  // north=0 clockwise ⇒ atan2(dx, -dy); y grows downward in screen space.
  const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

/**
 * Cumulative boundary END angles (deg) for `values` in array order.
 * boundaries[i] = end angle of segment i = start angle of segment (i+1).
 * Length n; boundaries[n-1] === 360 (≡ 0, the wrap boundary at north).
 */
export function boundaryAnglesDeg(values) {
  const total = sum(values);
  if (total <= 0) return values.map(() => 0);
  const out = [];
  let cum = 0;
  for (const v of values) {
    cum += v;
    out.push((cum / total) * DEG_PER_TURN);
  }
  return out;
}

/** Start angle (deg) of segment `idx` = end angle of the previous segment. */
export function segmentStartAngleDeg(values, idx) {
  const n = values.length;
  const bounds = boundaryAnglesDeg(values);
  const prev = (idx - 1 + n) % n;
  return bounds[prev] % 360; // prev = n-1 ⇒ 360 % 360 = 0 (north)
}

/** Clamp a span to at least `min`. */
export function clampSpan(value, min = MIN_SPAN_PCT) {
  return value < min ? min : value;
}

/** Signed clockwise delta (deg) from angle `from` to angle `to`, in [0, 360). */
function clockwiseDeltaDeg(from, to) {
  return (((to - from) % 360) + 360) % 360;
}

/**
 * The new span (%) the segment BEFORE boundary `boundaryIndex` would take if its
 * trailing boundary were dragged to `targetAngleDeg` — i.e. the raw angle→span
 * conversion, before any min clamp or neighbour split. Exposed for the overlay's
 * live readout and for tests.
 */
export function angleToSpanPct(values, boundaryIndex, targetAngleDeg) {
  const n = values.length;
  const A = ((boundaryIndex % n) + n) % n;
  const total = sum(values);
  if (total <= 0) return 0;
  const aStart = segmentStartAngleDeg(values, A);
  const spanDeg = clockwiseDeltaDeg(aStart, targetAngleDeg);
  return (spanDeg / DEG_PER_TURN) * total;
}

/**
 * NEIGHBOR mode. Drag boundary `boundaryIndex` (between segment A=boundaryIndex
 * and segment B=(boundaryIndex+1) mod n) to `targetAngleDeg`. Trades width
 * between A and B only; every other segment is untouched; the sum is preserved;
 * neither neighbour drops below `min`. If either neighbour is locked the
 * boundary can't move (returns the array unchanged).
 *
 * @returns {Array} a NEW segments array (inputs never mutated).
 */
export function applyNeighborResize(segments, boundaryIndex, targetAngleDeg, opts = {}) {
  const min = opts.min ?? MIN_SPAN_PCT;
  const n = segments.length;
  if (n < 2) return segments;
  const A = ((boundaryIndex % n) + n) % n;
  const B = (A + 1) % n;
  if (segments[A].resize_lock || segments[B].resize_lock) return segments;

  const values = segments.map((s) => s.value);
  const combined = values[A] + values[B];
  if (combined <= 0) return segments;
  if (combined < 2 * min) return segments; // no room to honour both floors

  let newA = angleToSpanPct(values, A, targetAngleDeg);
  newA = Math.min(Math.max(newA, min), combined - min); // clamp both floors
  const rA = round2(newA);
  const rB = round2(combined - rA);

  return segments.map((s, i) => {
    if (i === A) return { ...s, value: rA };
    if (i === B) return { ...s, value: rB };
    return s;
  });
}

/**
 * ALL-UNLOCKED mode. Drag boundary `boundaryIndex` toward `targetAngleDeg`. The
 * grower (the segment the boundary moves away from) gains; the gain is taken
 * EQUALLY from every OTHER unlocked segment (each donor gives the same share);
 * locked segments never move; the sum is preserved. Per-donor share is capped so
 * no donor drops below `min` (grower has no cap — it absorbs the whole spread).
 *
 * Deck pointer-math: the grabbed boundary moves by exactly one donor-share; with
 * N donors the grower gains N × share.
 *
 * @returns {Array} a NEW segments array (inputs never mutated).
 */
export function applyAllUnlockedResize(segments, boundaryIndex, targetAngleDeg, opts = {}) {
  const min = opts.min ?? MIN_SPAN_PCT;
  const n = segments.length;
  if (n < 2) return segments;
  const A = ((boundaryIndex % n) + n) % n;
  const B = (A + 1) % n;

  const values = segments.map((s) => s.value);
  const oldA = values[A];
  const rawNewA = angleToSpanPct(values, A, targetAngleDeg);
  // direction: A shrinks ⇒ B grows; A grows ⇒ B shrinks.
  const grower = rawNewA < oldA ? B : A;
  if (segments[grower].resize_lock) return segments; // locked can't grow either
  const boundaryMove = Math.abs(rawNewA - oldA); // desired per-donor share

  const donorIdx = segments
    .map((s, i) => i)
    .filter((i) => i !== grower && !segments[i].resize_lock);
  const N = donorIdx.length;
  if (N === 0) return segments;

  const maxShare = Math.min(...donorIdx.map((i) => values[i] - min));
  const share = Math.min(Math.max(boundaryMove, 0), Math.max(maxShare, 0));
  if (share <= 0) return segments;

  const donorSet = new Set(donorIdx);
  return segments.map((s, i) => {
    if (i === grower) return { ...s, value: round2(s.value + N * share) };
    if (donorSet.has(i)) return { ...s, value: round2(s.value - share) };
    return s;
  });
}

/**
 * Re-derive each segment's `data_value` from its (new) `value`, mirroring the
 * right-rail slider's persistence step (SegmentsTab handleSliderChange) so the
 * two inputs stay one model. Pure; caller passes the pre-drag total data.
 */
export function recomputeDataValues(segments, dataMultiplier = 1.01) {
  const totalData = sum(segments.map((s) => s.data_value || 0));
  if (totalData <= 0) return segments;
  return segments.map((s) => ({
    ...s,
    data_value: Math.max(1, Math.ceil((s.value / 100) * totalData * dataMultiplier)),
  }));
}
